import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

interface PreviewFAQ {
  id: number;
  category: string;
  categoryIcon: string;
  question: string;
  answer: string;
}

const PREVIEW_FAQS: PreviewFAQ[] = [
  {
    id: 1,
    category: '비용/견적',
    categoryIcon: 'ri-coins-line',
    question: '30평 전체 인테리어 비용은 얼마 정도 예상해야 하나요?',
    answer:
      '30평 기준 전체 인테리어는 일반적으로 3,500만원~5,500만원 수준입니다. 합리적인 중급 자재 기준으로는 약 4,000만원 내외가 평균적이며, 정확한 견적은 현장 실측 후 상세 내역서로 제공해 드립니다.',
  },
  {
    id: 2,
    category: '공정/일정',
    categoryIcon: 'ri-calendar-check-line',
    question: '30평 아파트 전체 공사 기간은 얼마나 걸리나요?',
    answer:
      '30평 기준 전체 인테리어 공사는 일반적으로 4~5주가 소요됩니다. 철거 → 설비/배관 → 목공 → 전기/조명 → 타일/바닥 → 도장/도배 → 가구/마감 순서로 진행되며, 현장 여건에 따라 일정이 달라질 수 있습니다.',
  },
  {
    id: 3,
    category: '창호',
    categoryIcon: 'ri-window-line',
    question: '이중창과 삼중창, 어떤 걸 선택해야 하나요?',
    answer:
      '일반 주거 환경에는 로이유리 이중창(60~100만원/개)이 비용 대비 효율이 높습니다. 도로변·지하철 인근 등 소음이 심한 환경이라면 삼중창(100~160만원/개)을 강력 추천드립니다.',
  },
  {
    id: 4,
    category: '계약/보증',
    categoryIcon: 'ri-shield-check-line',
    question: '공사 후 하자 보증 기간은 얼마나 되나요?',
    answer:
      '공사 완료 후 2년간 하자 보증을 제공합니다. 시공 불량으로 인한 균열, 들뜸, 누수 등의 하자는 무상으로 보수해 드리며, 하자 발생 시 48시간 이내 현장 확인 후 조치해 드립니다.',
  },
  {
    id: 5,
    category: '자재/상품',
    categoryIcon: 'ri-leaf-line',
    question: '친환경 자재만 사용할 수 있나요? 아이가 있어서 걱정됩니다.',
    answer:
      '전 공정에 걸쳐 친환경 자재 사용이 가능합니다. 저VOC 수성 페인트, 친환경 인증 벽지, E0 등급 이상 바닥재를 기본으로 사용하며, 영유아 가정을 위한 별도 친환경 패키지도 구성해 드립니다.',
  },
  {
    id: 6,
    category: '기타',
    categoryIcon: 'ri-building-line',
    question: '아파트 인테리어 시 관리사무소 신고가 필요한가요?',
    answer:
      '네, 대부분의 아파트는 공사 전 관리사무소에 신고 후 승인을 받아야 합니다. 저희가 공사 신고 서류 작성을 도와드리며, 엘리베이터 보양, 공사 시간 준수 등도 철저히 지킵니다.',
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  '비용/견적': '#e8a838',
  '공정/일정': '#5ba85a',
  '창호': '#4a9aba',
  '계약/보증': '#9b6bb5',
  '자재/상품': '#c9a84c',
  '기타': '#888888',
};

export default function FAQPreview() {
  const [openId, setOpenId] = useState<number | null>(null);

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: faqRef, getItemStyle } = useStaggerAnimation(PREVIEW_FAQS.length, { threshold: 0.05 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  const toggle = (id: number) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section className="py-28 md:py-36" style={{ backgroundColor: '#f7f5f3' }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">

        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col md:flex-row md:items-end justify-between mb-12 md:mb-16 gap-4 md:gap-6 text-center md:text-left"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(36px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div>
            <span
              className="text-xs tracking-[0.3em] uppercase font-semibold"
              style={{ color: '#c9a84c' }}
            >
              FAQ
            </span>
            <h2
              className="text-3xl md:text-5xl font-light text-[#1a1a1a] mt-3 md:mt-4 leading-tight"
              style={{ fontFamily: "'Noto Serif KR', serif" }}
            >
              자주 묻는 질문
            </h2>
            <p className="text-[#1a1a1a]/50 text-[11px] md:text-sm mt-2 md:mt-3 whitespace-nowrap overflow-hidden text-ellipsis">
              인테리어 공사 전 가장 많이 궁금해하시는 질문들을 모았습니다
            </p>
          </div>
          <Link
            to="/info"
            className="inline-flex items-center justify-center md:justify-start gap-2 text-[#1a1a1a] text-sm font-medium border-b border-[#1a1a1a]/30 pb-1 whitespace-nowrap cursor-pointer hover:text-[#c9a84c] hover:border-[#c9a84c] transition-colors mx-auto md:mx-0"
          >
            전체 FAQ 보기
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* FAQ Grid */}
        <div
          ref={faqRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 lg:grid-cols-2 gap-4"
        >
          {PREVIEW_FAQS.map((item, index) => {
            const isOpen = openId === item.id;
            const catColor = CATEGORY_COLORS[item.category] ?? '#c9a84c';

            return (
              <div
                key={item.id}
                className="rounded-2xl overflow-hidden transition-colors duration-300 cursor-pointer"
                style={{
                  backgroundColor: '#ffffff',
                  border: isOpen ? `1px solid ${catColor}55` : '1px solid #e8e4dc',
                  ...getItemStyle(index),
                }}
                onClick={() => toggle(item.id)}
              >
                {/* Question Row */}
                <div className="flex items-start gap-4 px-6 py-5">
                  <span
                    className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold mt-0.5 transition-all duration-300"
                    style={
                      isOpen
                        ? { backgroundColor: catColor, color: '#ffffff' }
                        : { backgroundColor: '#f5f2ec', color: '#1a1a1a66' }
                    }
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-5 h-5 flex items-center justify-center rounded-md shrink-0"
                        style={{ backgroundColor: `${catColor}18` }}
                      >
                        <i
                          className={`${item.categoryIcon} text-xs`}
                          style={{ color: catColor }}
                        />
                      </div>
                      <span
                        className="text-xs font-semibold"
                        style={{ color: catColor }}
                      >
                        {item.category}
                      </span>
                    </div>
                    <p
                      className="text-sm md:text-base font-semibold leading-snug transition-colors duration-200"
                      style={{ color: isOpen ? '#1a1a1a' : '#1a1a1acc' }}
                    >
                      {item.question}
                    </p>
                  </div>

                  <div
                    className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 mt-0.5"
                    style={
                      isOpen
                        ? { backgroundColor: `${catColor}18`, color: catColor, transform: 'rotate(180deg)' }
                        : { backgroundColor: '#f5f2ec', color: '#1a1a1a44' }
                    }
                  >
                    <i className="ri-arrow-down-s-line text-lg" />
                  </div>
                </div>

                {/* Answer */}
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: isOpen ? '300px' : '0px' }}
                >
                  <div className="px-6 pb-6 pt-0">
                    <div
                      className="h-px mb-4"
                      style={{ backgroundColor: `${catColor}22` }}
                    />
                    <div className="flex gap-3">
                      <div
                        className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full mt-0.5"
                        style={{ backgroundColor: `${catColor}15` }}
                      >
                        <i
                          className="ri-chat-1-line text-xs"
                          style={{ color: catColor }}
                        />
                      </div>
                      <p className="text-[#1a1a1a]/60 text-sm leading-relaxed flex-1">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className="mt-12 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6"
          style={{
            background: 'linear-gradient(135deg, #eeece9 0%, #e8e5e0 100%)',
            border: '1px solid #c9a84c33',
            opacity: ctaVisible ? 1 : 0,
            transform: ctaVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="flex items-center gap-5">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: '#c9a84c22' }}
            >
              <i className="ri-question-answer-line text-2xl" style={{ color: '#c9a84c' }} />
            </div>
            <div>
              <p className="text-[#2a2218] font-bold text-lg">원하는 답변을 찾지 못하셨나요?</p>
              <p className="text-[#6b5e4e] text-sm mt-1">
                전체 FAQ에서 더 많은 질문을 확인하거나, 전문 상담사에게 직접 문의하세요.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              to="/info"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer transition-colors"
              style={{ backgroundColor: '#c9a84c', color: '#0d0d0d' }}
            >
              <i className="ri-question-line" />
              전체 FAQ 보기
            </Link>
            <Link
              to="/consultation"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer transition-all"
              style={{
                backgroundColor: 'transparent',
                color: '#c9a84c',
                border: '1px solid #c9a84c55',
              }}
            >
              <i className="ri-chat-3-line" />
              전문가 상담 신청
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}
